<template>
    <main class="g10" dir="rtl">
        <!-- 1. Hero -->
        <section class="g10-hero">
            <div class="g10-hero__bg" aria-hidden="true">
                <span class="g10-hero__bg-code g10-hero__bg-code--1">{ }</span>
                <span class="g10-hero__bg-code g10-hero__bg-code--2">0101</span>
            </div>

            <div class="g10-wrap g10-hero__grid">
                <div class="g10-hero__content">
                    <span class="g10-badge g10-badge--green">{{ hero.badge }}</span>
                    <h1 class="seo-sr-only">أولى ثانوي Programming &amp; AI</h1>
                    <p class="g10-hero__title">{{ hero.title }}</p>
                    <p class="g10-hero__subtitle">{{ hero.subtitle }}</p>

                    <div class="g10-hero__actions">
                        <router-link :to="startRoute" class="g10-btn g10-btn--primary">
                            <span>{{ hero.primaryCta }}</span>
                            <lucide-icon name="ArrowLeft" class="g10-btn__icon" />
                        </router-link>
                        <a
                            :href="whatsappUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="g10-btn g10-btn--outline"
                        >
                            {{ hero.secondaryCta }}
                        </a>
                    </div>

                    <div class="g10-hero__chips">
                        <span v-for="chip in hero.chips" :key="chip" class="g10-chip">{{ chip }}</span>
                    </div>

                    <div class="g10-quick-guide" aria-label="طريقة سريعة لفهم النظام">
                        <article v-for="item in hero.quick" :key="item.title" class="g10-quick-guide__item">
                            <strong>{{ item.title }}</strong>
                            <span>{{ item.text }}</span>
                        </article>
                    </div>
                </div>

                <div class="g10-hero__visual">
                    <div class="g10-hero__glow" aria-hidden="true" />
                    <div class="g10-hero__edu-deco" aria-hidden="true">
                        <span class="g10-hero__edu-card" />
                        <span class="g10-hero__edu-laptop" />
                        <span class="g10-hero__edu-dot g10-hero__edu-dot--1" />
                        <span class="g10-hero__edu-dot g10-hero__edu-dot--2" />
                    </div>
                    <img
                        :src="heroImageSrc"
                        alt="محمد غنيم — البرمجة مع غنيم"
                        class="g10-hero__img"
                        width="480"
                        height="640"
                        loading="eager"
                        @error="onHeroImageError"
                    />
                </div>
            </div>
        </section>

        <!-- 2. Why -->
        <section class="g10-section g10-section--soft">
            <div class="g10-wrap">
                <header class="g10-section__header">
                    <h2 class="g10-section__title">{{ why.title }}</h2>
                    <p class="g10-section__subtitle">{{ why.subtitle }}</p>
                </header>
                <div class="g10-grid g10-grid--2 g10-grid--why">
                    <article
                        v-for="card in why.cards"
                        :key="card.title"
                        class="g10-card g10-card--illus"
                    >
                        <span
                            class="g10-card-illus"
                            :class="'g10-card-illus--' + card.illus"
                            aria-hidden="true"
                        />
                        <h3 class="g10-card__title">{{ card.title }}</h3>
                        <p class="g10-card__text">{{ card.text }}</p>
                    </article>
                </div>
                <div class="g10-roadmap-mini" aria-label="مسار التأسيس">
                    <span
                        v-for="(step, index) in why.roadmap"
                        :key="step"
                        class="g10-roadmap-mini__step"
                    >
                        <span class="g10-roadmap-mini__num">{{ index + 1 }}</span>
                        <span class="g10-roadmap-mini__label">{{ step }}</span>
                    </span>
                </div>
                <p class="g10-strong-line">{{ why.closing }}</p>
            </div>
        </section>

        <!-- 3. Topics -->
        <section class="g10-section">
            <div class="g10-wrap">
                <header class="g10-section__header">
                    <h2 class="g10-section__title">{{ topics.title }}</h2>
                    <p class="g10-section__subtitle">{{ topics.subtitle }}</p>
                </header>
                <div class="g10-grid g10-grid--topics">
                    <article
                        v-for="item in topics.items"
                        :key="item.title"
                        class="g10-topic"
                    >
                        <span
                            class="g10-topic__icon"
                            :class="'g10-topic__icon--' + item.illus"
                            aria-hidden="true"
                        />
                        <span class="g10-topic__tag">{{ item.tag }}</span>
                        <h3 class="g10-topic__title">{{ item.title }}</h3>
                        <p class="g10-topic__text">{{ item.text }}</p>
                    </article>
                </div>
                <p class="g10-bridge-line">{{ topics.bridge }}</p>
                <p class="g10-curriculum-link">
                    <router-link :to="{ name: 'grade-10-programming-ai-curriculum' }">
                        تعرف على منهج البرمجة أولى ثانوي بالكامل — الكتاب والتوزيع والدروس المطلوبة
                    </router-link>
                </p>
            </div>
        </section>

        <!-- 4. System -->
        <section class="g10-section g10-section--soft">
            <div class="g10-wrap">
                <header class="g10-section__header">
                    <h2 class="g10-section__title">{{ system.title }}</h2>
                    <p class="g10-section__subtitle">{{ system.subtitle }}</p>
                </header>
                <div class="g10-grid g10-grid--2">
                    <article
                        v-for="card in system.cards"
                        :key="card.title"
                        class="g10-card g10-card--tool"
                    >
                        <span
                            class="g10-card-illus g10-card-illus--sm"
                            :class="'g10-card-illus--' + card.illus"
                            aria-hidden="true"
                        />
                        <h3 class="g10-card__title">{{ card.title }}</h3>
                        <p class="g10-card__text">{{ card.text }}</p>
                    </article>
                </div>
                <div class="g10-flow-row" aria-label="خطوات النظام">
                    <span
                        v-for="(step, index) in system.flow"
                        :key="step"
                        class="g10-flow-row__item"
                    >
                        <span v-if="index > 0" class="g10-flow-row__arrow" aria-hidden="true">←</span>
                        <span class="g10-flow-row__pill">{{ step }}</span>
                    </span>
                </div>
                <div class="g10-lesson-stack" aria-label="تفاصيل الحصة">
                    <article
                        v-for="(step, index) in system.lessonSteps"
                        :key="step.title"
                        class="g10-lesson-stack__item"
                    >
                        <span>{{ index + 1 }}</span>
                        <div>
                            <strong>{{ step.title }}</strong>
                            <p>{{ step.text }}</p>
                        </div>
                    </article>
                </div>
                <p class="g10-strong-line">{{ system.closing }}</p>
            </div>
        </section>

        <!-- 5. Study -->
        <section class="g10-section">
            <div class="g10-wrap">
                <div class="g10-split g10-split--study">
                    <div>
                        <header class="g10-section__header g10-section__header--tight">
                            <h2 class="g10-section__title">{{ study.title }}</h2>
                            <p class="g10-section__subtitle">{{ study.subtitle }}</p>
                        </header>
                        <ol class="g10-checklist">
                            <li v-for="(step, index) in study.steps" :key="step" class="g10-checklist__item">
                                <span class="g10-checklist__num">{{ index + 1 }}</span>
                                <span>{{ step }}</span>
                            </li>
                        </ol>
                        <p class="g10-highlight-card g10-highlight-card--inline">{{ study.highlight }}</p>
                    </div>
                    <aside class="g10-study-panel" aria-hidden="true">
                        <div class="g10-study-panel__head">خطة الأسبوع</div>
                        <div class="g10-study-panel__row g10-study-panel__row--done">
                            <span>شرح</span>
                            <span class="g10-study-panel__check" />
                        </div>
                        <div class="g10-study-panel__row g10-study-panel__row--done">
                            <span>حل</span>
                            <span class="g10-study-panel__check" />
                        </div>
                        <div class="g10-study-panel__row">
                            <span>مراجعة</span>
                            <span class="g10-study-panel__dot" />
                        </div>
                        <div class="g10-study-panel__row">
                            <span>كود</span>
                            <span class="g10-study-panel__dot" />
                        </div>
                    </aside>
                </div>
            </div>
        </section>

        <!-- 6. Parents -->
        <section class="g10-section g10-section--soft">
            <div class="g10-wrap g10-parents">
                <header class="g10-section__header g10-section__header--tight">
                    <h2 class="g10-section__title">{{ parents.title }}</h2>
                </header>
                <p class="g10-parents__text">{{ parents.text }}</p>
                <ul class="g10-bullets g10-bullets--check">
                    <li v-for="point in parents.points" :key="point">{{ point }}</li>
                </ul>
                <p class="g10-highlight-inline">{{ parents.highlight }}</p>
            </div>
        </section>

        <!-- 7. FAQ -->
        <section class="g10-section">
            <div class="g10-wrap">
                <header class="g10-section__header">
                    <h2 class="g10-section__title">{{ faq.title }}</h2>
                </header>
                <div class="g10-faq">
                    <div
                        v-for="(item, index) in faq.items"
                        :key="item.q"
                        class="g10-faq__item"
                        :class="{ 'g10-faq__item--open': openFaq === index }"
                    >
                        <button
                            type="button"
                            class="g10-faq__trigger"
                            :aria-expanded="openFaq === index ? 'true' : 'false'"
                            @click="toggleFaq(index)"
                        >
                            <span class="g10-faq__question">{{ item.q }}</span>
                            <span class="g10-faq__chevron" aria-hidden="true" />
                        </button>
                        <div
                            v-show="openFaq === index"
                            :id="'g10-faq-panel-' + index"
                            class="g10-faq__panel"
                            role="region"
                        >
                            <p>{{ item.a }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 8. Final CTA -->
        <section class="g10-section g10-section--cta">
            <div class="g10-wrap">
                <div class="g10-cta-card">
                    <h2 class="g10-cta-card__title">{{ finalCta.title }}</h2>
                    <p class="g10-cta-card__subtitle">{{ finalCta.subtitle }}</p>
                    <p class="g10-cta-card__tag">{{ finalCta.hashtag }}</p>
                    <div class="g10-cta-card__actions">
                        <router-link :to="startRoute" class="g10-btn g10-btn--primary g10-btn--lg">
                            <span>{{ finalCta.primaryCta }}</span>
                            <lucide-icon name="ArrowLeft" class="g10-btn__icon" />
                        </router-link>
                        <a
                            :href="whatsappUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="g10-btn g10-btn--outline g10-btn--lg"
                        >
                            {{ finalCta.secondaryCta }}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import LucideIcon from '../LucideIcon.vue';
import {
    GRADE_10_HERO_IMAGES,
    grade10Hero,
    grade10Why,
    grade10Topics,
    grade10System,
    grade10Study,
    grade10Parents,
    grade10Faq,
    grade10FinalCta,
} from './grade-10-data';

let heroAssetFallback = null;
try {
    heroAssetFallback = require('@/assets/welcome/ghonaim-hero.webp');
} catch (e) {
    heroAssetFallback = null;
}

export default {
    name: 'Grade10Landing',
    components: { LucideIcon },
    props: {
        coursesRoute: {
            type: Object,
            required: true,
        },
        whatsappUrl: {
            type: String,
            required: true,
        },
        startRoute: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            hero: grade10Hero,
            why: grade10Why,
            topics: grade10Topics,
            system: grade10System,
            study: grade10Study,
            parents: grade10Parents,
            faq: grade10Faq,
            finalCta: grade10FinalCta,
            heroImageSrc: heroAssetFallback || GRADE_10_HERO_IMAGES[0],
            heroImageFallbackIndex: 0,
            openFaq: null,
        };
    },
    methods: {
        toggleFaq(index) {
            this.openFaq = this.openFaq === index ? null : index;
        },
        onHeroImageError() {
            const nextPublicIndex = this.heroImageFallbackIndex + 1;
            if (nextPublicIndex < GRADE_10_HERO_IMAGES.length) {
                this.heroImageFallbackIndex = nextPublicIndex;
                this.heroImageSrc = GRADE_10_HERO_IMAGES[nextPublicIndex];
                return;
            }
            if (heroAssetFallback) {
                this.heroImageSrc = heroAssetFallback;
            }
        },
    },
};
</script>
